import type { Checkpoint } from '@/domains/checkpoint/checkpoint.types';
import type {
  Exception,
  CheckpointParameter,
} from '@/domains/checkpoint/checkpoint.types';
import type { OnboardingCheckpointViewModel } from '@/features/onboarding/types/onboarding.types';
import {
  getCheckpointVersion,
  makeCheckpointKey,
} from '@/lib/checkpointKeys';
import { getCurrentUserPrincipal } from '@/lib/currentUser';

import type { SiteComplianceConfiguration } from './siteComplianceConfiguration.types';

const SITE_COMPLIANCE_DRAFT_PARAMETER_NAMES_TO_REVIEW = new Set([
  'mandatorySubnets',
  'wkConnectivityMode',
  'vdcCidrs',
  'firewallPrefixGroup',
]);

const cloneCheckpointParameters = (parameters: CheckpointParameter[]) =>
  parameters.map((parameter) => ({ ...parameter }));

const cloneCheckpointExceptions = (exceptions: Exception[] | null) =>
  exceptions ? exceptions.map((exception) => ({ ...exception })) : null;

export const getSortedSiteComplianceConfigurations = (
  configurations?: SiteComplianceConfiguration[] | null,
) => {
  if (!configurations) {
    return [];
  }

  return configurations
    .slice()
    .sort(
      (left, right) =>
        new Date(right.createdAt).getTime() -
        new Date(left.createdAt).getTime(),
    );
};

export const getLatestSiteComplianceConfiguration = (
  sortedConfigurations: SiteComplianceConfiguration[],
) => {
  return sortedConfigurations[0] ?? null;
};

export const getPreviousSiteComplianceConfiguration = (
  sortedConfigurations: SiteComplianceConfiguration[],
) => {
  if (sortedConfigurations.length <= 1) {
    return null;
  }

  return sortedConfigurations[1];
};

export const hasValidSiteComplianceCheckpointParameters = (
  checkpoint: Checkpoint,
) => {
  if (!checkpoint.parameters?.length || checkpoint.exceptions?.length) {
    return true;
  }

  return checkpoint.parameters.every((parameter) => {
    if (parameter.isOptional || parameter.isRuntime || !checkpoint.isScored) {
      return true;
    }

    return `${parameter.paramValue ?? ''}`.trim() !== '';
  });
};

export const getSiteComplianceCheckpointExceptionApprovalStatus = (
  checkpoint: Checkpoint,
) => {
  if (!checkpoint.exceptions?.length) {
    return null;
  }

  if (checkpoint.exceptions[0].isApproved === null) {
    return 'pending';
  }

  if (checkpoint.exceptions[0].isApproved) {
    return 'approved';
  }

  return 'rejected';
};

export const isNewSiteComplianceCheckpoint = (
  checkpoint: Checkpoint,
  previousConfiguration: SiteComplianceConfiguration | null,
) => {
  if (!previousConfiguration) {
    return true;
  }

  const checkpointVersion = getCheckpointVersion(checkpoint);

  return !previousConfiguration.checkpoints.some(
    (currentCheckpoint) =>
      currentCheckpoint.id === checkpoint.id &&
      getCheckpointVersion(currentCheckpoint) === checkpointVersion,
  );
};

export const filterSiteComplianceCheckpoints = ({
  checkpointSearch,
  checkpoints,
  previousConfiguration,
}: {
  checkpointSearch: string;
  checkpoints: Checkpoint[];
  previousConfiguration: SiteComplianceConfiguration | null;
}) => {
  const normalizedSearchTerms = checkpointSearch
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (!normalizedSearchTerms.length) {
    return checkpoints;
  }

  return checkpoints.filter((checkpoint) => {
    const searchableFields = [
      checkpoint.id,
      checkpoint.description,
      checkpoint.category,
      checkpoint.accountable,
      checkpoint.owner,
      checkpoint.referenceSystem,
      checkpoint.displayName,
      checkpoint.isScored ? 'scored' : 'preview',
      isNewSiteComplianceCheckpoint(checkpoint, previousConfiguration)
        ? 'new'
        : '',
      !hasValidSiteComplianceCheckpointParameters(checkpoint)
        ? 'requires parameters'
        : '',
      checkpoint.exceptions?.length ? 'exception' : '',
      getSiteComplianceCheckpointExceptionApprovalStatus(checkpoint) ===
      'pending'
        ? 'exception pending approval'
        : '',
      getSiteComplianceCheckpointExceptionApprovalStatus(checkpoint) ===
      'approved'
        ? 'exception approved'
        : '',
      getSiteComplianceCheckpointExceptionApprovalStatus(checkpoint) ===
      'rejected'
        ? 'exception rejected'
        : '',
      String(getCheckpointVersion(checkpoint) ?? 'n/a'),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return normalizedSearchTerms.every((term) =>
      searchableFields.includes(term),
    );
  });
};

export const getSiteComplianceCheckpointsCountWithInvalidParameters = (
  latestConfiguration: SiteComplianceConfiguration | null,
) => {
  if (!latestConfiguration) {
    return 0;
  }

  return latestConfiguration.checkpoints.reduce((count, checkpoint) => {
    if (!checkpoint.parameters?.length) {
      return count;
    }

    return hasValidSiteComplianceCheckpointParameters(checkpoint)
      ? count
      : count + 1;
  }, 0);
};

export const getSiteComplianceCheckpointVersionLabel = (
  checkpoint: Checkpoint,
) => {
  const version = getCheckpointVersion(checkpoint);
  return version === null || version === undefined ? 'N/A' : `${version}`;
};

export const getSiteComplianceCheckpointRowKey = (checkpoint: Checkpoint) =>
  makeCheckpointKey(checkpoint.id, getCheckpointVersion(checkpoint)).replace(
    /[^a-zA-Z0-9_-]/g,
    '-',
  );

export const mapSiteComplianceCheckpointToOnboardingViewModel = (
  checkpoint: Checkpoint,
  previousConfiguration: SiteComplianceConfiguration | null,
): OnboardingCheckpointViewModel<Checkpoint> => ({
  accountable: checkpoint.accountable || null,
  category: checkpoint.category || null,
  description: checkpoint.description || '',
  exceptionApprovalStatus:
    getSiteComplianceCheckpointExceptionApprovalStatus(checkpoint),
  id: checkpoint.id,
  isNew: isNewSiteComplianceCheckpoint(checkpoint, previousConfiguration),
  isPreview: !checkpoint.isScored,
  key: getSiteComplianceCheckpointRowKey(checkpoint),
  rawCheckpoint: checkpoint,
  requiresParameters: !hasValidSiteComplianceCheckpointParameters(checkpoint),
  versionLabel: getSiteComplianceCheckpointVersionLabel(checkpoint),
});

export const mapSiteComplianceCheckpointsToOnboardingViewModels = (
  checkpoints: Checkpoint[],
  previousConfiguration: SiteComplianceConfiguration | null,
) =>
  checkpoints.map((checkpoint) =>
    mapSiteComplianceCheckpointToOnboardingViewModel(
      checkpoint,
      previousConfiguration,
    ),
  );

export const createSiteComplianceDraftConfiguration = ({
  configuration,
  siteId,
}: {
  configuration: SiteComplianceConfiguration | null;
  siteId: string;
}): SiteComplianceConfiguration => {
  const createdAt = new Date().toISOString();
  const sourceConfiguration = configuration ?? {
    siteId,
    isDraft: false,
    checkpoints: [],
    configurationId: `site-config-${siteId}-empty-${Date.now()}`,
    createdAt,
    updatedAt: null,
    createdBy: getCurrentUserPrincipal(),
    sourceConfigurationId: null,
  };

  return {
    ...sourceConfiguration,
    isDraft: true,
    configurationId: `site-config-${siteId}-draft-${Date.now()}`,
    createdAt,
    updatedAt: null,
    createdBy: getCurrentUserPrincipal(),
    sourceConfigurationId: configuration?.configurationId ?? null,
    checkpoints: sourceConfiguration.checkpoints.map((checkpoint) => ({
      ...checkpoint,
      parameters: checkpoint.parameters.map((parameter) => ({
        ...parameter,
        paramValue: SITE_COMPLIANCE_DRAFT_PARAMETER_NAMES_TO_REVIEW.has(
          parameter.paramName,
        )
          ? ''
          : parameter.paramValue,
      })),
      exceptions: cloneCheckpointExceptions(checkpoint.exceptions),
    })),
  };
};

export const updateSiteComplianceCheckpointParameters = ({
  checkpointId,
  checkpointVersion,
  configuration,
  parameters,
}: {
  checkpointId: string;
  checkpointVersion?: string | number | null;
  configuration: SiteComplianceConfiguration;
  parameters: CheckpointParameter[];
}) => ({
  ...configuration,
  updatedAt: new Date().toISOString(),
  checkpoints: configuration.checkpoints.map((checkpoint) => {
    const currentKey = makeCheckpointKey(
      checkpoint.id,
      getCheckpointVersion(checkpoint),
    );
    const targetKey = makeCheckpointKey(checkpointId, checkpointVersion);

    if (currentKey !== targetKey) {
      return checkpoint;
    }

    return {
      ...checkpoint,
      parameters: cloneCheckpointParameters(parameters),
    };
  }),
});
